import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../config.js';

const MercadoPagoPayment = ({ 
  title = "Valuación Inmobiliaria", 
  price, 
  originalPrice,
  discountPercent,
  description, 
  userEmail,
  smp,
  paymentType = "analysis",
  onPaymentSuccess,
  onPaymentError,
  // Property data for premium valuation
  propertyData = null
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [paymentOpened, setPaymentOpened] = useState(false);
  const [valuationResults, setValuationResults] = useState(null);
  const [valuationComplete, setValuationComplete] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [freePaymentApproved, setFreePaymentApproved] = useState(false);
  
  // Promo code states
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValidating, setPromoCodeValidating] = useState(false);
  const [promoCodeValid, setPromoCodeValid] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [finalPrice, setFinalPrice] = useState(price);

  // Auto-run valuation for premium (but don't auto-redirect)
  useEffect(() => {
    if (paymentType === 'premium_valuation' && !valuationResults && !valuationComplete && propertyData && price) {
      console.log('🚀 Auto-running premium valuation...');
      runValuation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, propertyData, price]);

  // Update final price when price changes
  useEffect(() => {
    if (!promoCodeValid) {
      setFinalPrice(price);
    }
  }, [price, promoCodeValid]);

  const validatePromoCode = async (code) => {
    if (!code || code.trim() === '') {
      setPromoCodeError('');
      setPromoCodeValid(false);
      setPromoDiscount(null);
      setFinalPrice(price);
      return;
    }

    setPromoCodeValidating(true);
    setPromoCodeError('');

    try {
      const response = await axios.post(`${apiBaseUrl}/api/promo-codes/validate`, {
        code: code.trim()
      });

      if (response.data.valid) {
        setPromoCodeValid(true);
        setPromoDiscount({
          ...response.data.promoCode,
          discountAmount: response.data.pricing.discount,
          originalPrice: response.data.pricing.originalPrice,
          finalPrice: response.data.pricing.finalPrice
        });
        setFinalPrice(response.data.pricing.finalPrice);
        setPromoCodeError('');
      } else {
        setPromoCodeValid(false);
        setPromoDiscount(null);
        setFinalPrice(price);
        setPromoCodeError(response.data.error || 'Código promocional inválido');
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoCodeValid(false);
      setPromoDiscount(null);
      setFinalPrice(price);
      setPromoCodeError(error.response?.data?.message || 'Error al validar el código');
    } finally {
      setPromoCodeValidating(false);
    }
  };

  const handlePromoCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setPromoCode(code);
  };

  const handleApplyPromoCode = () => {
    validatePromoCode(promoCode);
  };

  const handleRemovePromoCode = () => {
    setPromoCode('');
    setPromoCodeValid(false);
    setPromoDiscount(null);
    setPromoCodeError('');
    setFinalPrice(price);
  };

  const runValuation = async () => {
    try {
      setLoading(true);
      setLoadingMessage('🔍 Buscando propiedades similares en el mercado...');
      console.log('💎 Running premium valuation...');
      
      const valuationResponse = await axios.post(
        `${apiBaseUrl}/api/premium_valuation`,
        propertyData
      );
      
      if (valuationResponse.data.success) {
        const valuation = valuationResponse.data;
        setValuationResults(valuation);
        setValuationComplete(true);
        console.log('✅ Valuation completed:', {
          range: `$${valuation.valorMinimo}-$${valuation.valorMaximo}`,
          properties: valuation.marketValuation?.propiedadesAnalizadas || 0
        });
        
        // Don't create payment preference automatically anymore
        // Wait for user to enter promo code and click button
        setLoading(false);
        setLoadingMessage('');
      } else {
        throw new Error('Valuation failed');
      }
    } catch (error) {
      console.error('❌ Valuation error:', error);
      setError('No pudimos completar la valuación. Por favor intenta nuevamente.');
      setLoading(false);
    }
  };

  const createPaymentPreference = async (valuation) => {
    try {
      const payload = {
        title: title,
        price: finalPrice, // Use final price (with discount if applied)
        description: description,
        userEmail: userEmail,
        address: propertyData?.addressD || smp,
        addressD: propertyData?.addressD || smp,
        zone_c: propertyData?.zone_c,
        property: propertyData?.property,
        operation: propertyData?.operation,
        amb: propertyData?.amb,
        garage: propertyData?.garage,
        condition: propertyData?.condition,
        antiguedad: propertyData?.antiguedad,
        valuationType: propertyData?.valuationType,
        sc: propertyData?.sc,
        st: propertyData?.st,
        sd: propertyData?.sd,
        sterre: propertyData?.sterre,
        barbecue: propertyData?.barbecue,
        pool: propertyData?.pool,
        sum: propertyData?.sum,
        currency: 'ARS',
        valuationResults: valuation,
        termsAccepted: true, // Always true since button is disabled until checked
        termsAcceptedAt: new Date().toISOString(),
        promoCode: promoCodeValid ? promoCode : undefined // Include promo code if valid
      };
      
      const response = await axios.post(`${apiBaseUrl}/api/create_premium_payment`, payload);

      if (response.data.success) {
        // Check if payment is free (100% discount with promo code)
        if (response.data.isFree) {
          console.log('🎉 Free payment approved! No MercadoPago needed.');
          
          // Save approved payment info
          const paymentInfo = {
            externalReference: response.data.externalReference,
            smp: smp,
            timestamp: Date.now(),
            pendingPayment: false,
            approved: true,
            isFree: true
          };
          
          localStorage.setItem('approvedMercadoPagoPayment', JSON.stringify(paymentInfo));
          localStorage.setItem('lastAnalyzedSmp', smp);
          localStorage.removeItem('pendingMercadoPagoPayment');
          
          // Mark as free payment approved
          setFreePaymentApproved(true);
          setLoading(false);
          setLoadingMessage('');
          
          if (onPaymentSuccess) {
            onPaymentSuccess({
              status: 'approved',
              externalReference: response.data.externalReference,
              isFree: true,
              valuationResults: valuation
            });
          }
        } else {
          // Regular paid flow - save payment info and prepare URL
          const paymentInfo = {
            preferenceId: response.data.preferenceId,
            externalReference: response.data.externalReference,
            smp: smp,
            timestamp: Date.now(),
            pendingPayment: true
          };
          
          localStorage.setItem('pendingMercadoPagoPayment', JSON.stringify(paymentInfo));
          localStorage.setItem('lastAnalyzedSmp', smp);
          
          // Store payment URL and redirect immediately
          setPaymentUrl(response.data.initPoint);
          setLoading(false);
          setLoadingMessage('');
          
          // Redirect to MercadoPago
          console.log('💳 Redirecting to MercadoPago with discounted price...');
          window.location.href = response.data.initPoint;
        }
      } else {
        throw new Error('Failed to create payment preference');
      }
    } catch (error) {
      console.error('❌ Payment creation error:', error);
      setError('Error al preparar el pago. Por favor intenta nuevamente.');
      setLoading(false);
    }
  };

  const goToPayment = async () => {
    if (!termsAccepted) {
      alert('Por favor acepta los términos y condiciones');
      return;
    }

    // If payment URL already exists, just redirect
    if (paymentUrl) {
      console.log('💳 Redirecting to MercadoPago...');
      window.location.href = paymentUrl;
      return;
    }

    // Otherwise, create payment preference first
    try {
      setLoading(true);
      setLoadingMessage('💳 Preparando pago...');
      await createPaymentPreference(valuationResults);
      
      // After creating preference, paymentUrl will be set
      // The redirect will happen automatically or user can click again
    } catch (error) {
      console.error('❌ Error creating payment preference:', error);
      setError('Error al preparar el pago. Por favor intenta nuevamente.');
      setLoading(false);
    }
  };

  // Handle free payment - approve without MercadoPago
  const handleFreePayment = async () => {
    if (!termsAccepted) {
      alert('Por favor acepta los términos y condiciones');
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage('🎉 Procesando pago gratuito...');
      
      // Create payment with promo code (backend will detect it's free)
      const payload = {
        title: title,
        price: finalPrice,
        description: description,
        userEmail: userEmail,
        address: propertyData?.addressD || smp,
        addressD: propertyData?.addressD || smp,
        zone_c: propertyData?.zone_c,
        property: propertyData?.property,
        operation: propertyData?.operation,
        amb: propertyData?.amb,
        garage: propertyData?.garage,
        condition: propertyData?.condition,
        antiguedad: propertyData?.antiguedad,
        valuationType: propertyData?.valuationType,
        sc: propertyData?.sc,
        st: propertyData?.st,
        sd: propertyData?.sd,
        sterre: propertyData?.sterre,
        barbecue: propertyData?.barbecue,
        pool: propertyData?.pool,
        sum: propertyData?.sum,
        currency: 'ARS',
        valuationResults: valuationResults,
        termsAccepted: true,
        termsAcceptedAt: new Date().toISOString(),
        promoCode: promoCodeValid ? promoCode : undefined
      };
      
      const response = await axios.post(`${apiBaseUrl}/api/create_premium_payment`, payload);

      if (response.data.success && response.data.isFree) {
        console.log('🎉 Free payment approved!');
        
        // Save approved payment info
        const paymentInfo = {
          externalReference: response.data.externalReference,
          smp: smp,
          timestamp: Date.now(),
          pendingPayment: false,
          approved: true,
          isFree: true
        };
        
        localStorage.setItem('approvedMercadoPagoPayment', JSON.stringify(paymentInfo));
        localStorage.setItem('lastAnalyzedSmp', smp);
        localStorage.removeItem('pendingMercadoPagoPayment');
        
        // Mark as approved and trigger success callback
        setFreePaymentApproved(true);
        setLoading(false);
        setLoadingMessage('');
        
        if (onPaymentSuccess) {
          onPaymentSuccess({
            status: 'approved',
            externalReference: response.data.externalReference,
            isFree: true,
            valuationResults: valuationResults
          });
        }
      } else {
        throw new Error('Error processing free payment');
      }
    } catch (error) {
      console.error('❌ Error processing free payment:', error);
      setError('Error al procesar el pago gratuito. Por favor intenta nuevamente.');
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // For non-premium payments (SMP Analysis)
  const createPayment = async () => {
    try {
      setLoading(true);
      setError('');
      
      const endpoint = `${apiBaseUrl}/api/create_payment`;
      const payload = {
        title: title,
        price: price,
        description: description,
        userEmail: userEmail,
        smp: smp,
        currency: 'ARS'
      };
      
      const response = await axios.post(endpoint, payload);

      if (response.data.success) {
        const paymentInfo = {
          preferenceId: response.data.preferenceId,
          externalReference: response.data.externalReference,
          smp: smp,
          timestamp: Date.now(),
          pendingPayment: true
        };
        
        localStorage.setItem('pendingMercadoPagoPayment', JSON.stringify(paymentInfo));
        localStorage.setItem('lastAnalyzedSmp', smp);
        
        window.open(response.data.initPoint, '_blank');
        setPaymentOpened(true);
      } else {
        throw new Error('Failed to create payment preference');
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error creating payment';
      setError(errorMessage);
      
      if (onPaymentError) {
        onPaymentError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      border: '2px solid #00b4d8',
      borderRadius: '12px',
      padding: '20px',
      backgroundColor: '#f8f9ff',
      margin: '20px 0',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '15px',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <img 
          src="https://http2.mlstatic.com/storage/mobile-on-demand-resources/image/web-private-nav-mp-logo_1X?updatedAt=1746639317789" 
          alt="MercadoPago" 
          style={{ height: '40px', maxWidth: '160px', objectFit: 'contain' }}
        />
        <span style={{ 
          fontSize: '16px', 
          fontWeight: 'bold', 
          color: '#2c3e50',
          flex: '1',
          minWidth: '0'
        }}>
          Pagar con MercadoPago
        </span>
      </div>

      <div style={{
        backgroundColor: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '15px',
        border: '1px solid #e9ecef',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          color: '#2c3e50',
          marginBottom: '8px',
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}>
          {title}
        </div>
        {description && (
          <div style={{ 
            fontSize: '13px', 
            color: '#6c757d',
            marginBottom: '12px',
            lineHeight: '1.5',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            {description}
          </div>
        )}
        {/* Show pricing - with promo discount if applied, or system discount if available */}
        {promoCodeValid && promoDiscount ? (
          // Promo code discount applied
          <div>
            <div style={{ 
              fontSize: '15px', 
              fontWeight: '500', 
              color: '#6c757d',
              textDecoration: 'line-through',
              marginBottom: '5px'
            }}>
              ${price?.toLocaleString('es-AR')} ARS
            </div>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: finalPrice === 0 ? '#10b981' : '#28a745',
              marginBottom: '5px'
            }}>
              {finalPrice === 0 ? 'GRATIS' : `$${finalPrice?.toLocaleString('es-AR')} ARS`}
            </div>
            <div style={{
              fontSize: '13px',
              color: finalPrice === 0 ? '#10b981' : '#28a745',
              fontWeight: '600',
              marginTop: '5px'
            }}>
              🎉 {finalPrice === 0 
                ? '¡Servicio completamente gratis con tu código!' 
                : `Ahorrás $${promoDiscount.discountAmount?.toLocaleString('es-AR')} con código promocional`}
            </div>
          </div>
        ) : discountPercent > 0 && originalPrice ? (
          // System discount (no promo code)
          <div>
            <div style={{ 
              fontSize: '15px', 
              fontWeight: '500', 
              color: '#6c757d',
              textDecoration: 'line-through',
              marginBottom: '5px'
            }}>
              ${originalPrice?.toLocaleString('es-AR')} ARS
            </div>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: '#28a745',
              marginBottom: '5px'
            }}>
              ${price?.toLocaleString('es-AR')} ARS
            </div>
            <div style={{
              fontSize: '13px',
              color: '#28a745',
              fontWeight: '600',
              marginTop: '5px'
            }}>
              🎉 Ahorrás ${(originalPrice - price)?.toLocaleString('es-AR')} ({discountPercent}% OFF)
            </div>
          </div>
        ) : (
          // No discounts
          <div style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: '#28a745'
          }}>
            ${finalPrice?.toLocaleString('es-AR')} ARS
          </div>
        )}
      </div>

      {/* Promo Code Section */}
      <div style={{
        backgroundColor: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '15px',
        border: '1px solid #e9ecef'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#2c3e50',
          marginBottom: '10px'
        }}>
          🎟️ ¿Tenés un código promocional?
        </div>
        
        {!promoCodeValid ? (
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <input
              type="text"
              value={promoCode}
              onChange={handlePromoCodeChange}
              placeholder="Ingresá tu código"
              disabled={loading || promoCodeValidating}
              style={{
                flex: '1',
                minWidth: '150px',
                padding: '10px 12px',
                fontSize: '14px',
                border: `2px solid ${promoCodeError ? '#dc3545' : '#ced4da'}`,
                borderRadius: '6px',
                outline: 'none',
                textTransform: 'uppercase'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleApplyPromoCode();
                }
              }}
            />
            <button
              onClick={handleApplyPromoCode}
              disabled={!promoCode.trim() || loading || promoCodeValidating}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: (!promoCode.trim() || loading || promoCodeValidating) ? '#6c757d' : '#667eea',
                border: 'none',
                borderRadius: '6px',
                cursor: (!promoCode.trim() || loading || promoCodeValidating) ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background-color 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (promoCode.trim() && !loading && !promoCodeValidating) {
                  e.target.style.backgroundColor = '#5568d3';
                }
              }}
              onMouseLeave={(e) => {
                if (promoCode.trim() && !loading && !promoCodeValidating) {
                  e.target.style.backgroundColor = '#667eea';
                }
              }}
            >
              {promoCodeValidating ? '⏳' : 'Aplicar'}
            </button>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px',
            backgroundColor: '#d4edda',
            borderRadius: '6px',
            border: '1px solid #28a745'
          }}>
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#155724'
              }}>
                ✅ {promoCode} aplicado
              </div>
              <div style={{
                fontSize: '13px',
                color: '#155724',
                marginTop: '4px'
              }}>
                {promoDiscount?.discountType === 'free' 
                  ? '🎉 ¡Servicio gratis!' 
                  : `Descuento: $${promoDiscount?.discountAmount?.toLocaleString('es-AR')}`}
              </div>
            </div>
            <button
              onClick={handleRemovePromoCode}
              disabled={loading}
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#dc3545',
                backgroundColor: 'white',
                border: '1px solid #dc3545',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              Quitar
            </button>
          </div>
        )}

        {promoCodeError && (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#ffe6e6',
            color: '#d8000c',
            borderRadius: '4px',
            fontSize: '13px'
          }}>
            ❌ {promoCodeError}
          </div>
        )}
      </div>

      {error && (
        <div style={{
          backgroundColor: '#ffe6e6',
          color: '#d8000c',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '15px',
          fontSize: '14px'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '15px',
          fontSize: '15px',
          textAlign: 'center',
          fontWeight: '500',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '3px solid #856404',
            borderTop: '3px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          {loadingMessage}
        </div>
      )}

      {/* Success State - Free Payment Approved */}
      {!loading && valuationComplete && valuationResults && freePaymentApproved && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '15px',
          border: '2px solid #28a745',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '15px' }}>🎉</div>
          <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '10px' }}>
            ¡Pago Aprobado Gratis!
          </div>
          <div style={{ fontSize: '16px', marginBottom: '15px' }}>
            Tu código promocional ha sido aplicado exitosamente.
          </div>
          <div style={{ fontSize: '14px', color: '#155724' }}>
            ✅ Los resultados completos están disponibles ahora
          </div>
        </div>
      )}

      {/* Success State - Valuation Complete (Blurred Preview) - Only show if NOT free */}
      {!loading && valuationComplete && valuationResults && !freePaymentApproved && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '15px',
          border: '2px solid #28a745'
        }}>
          <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '15px', textAlign: 'center' }}>
            ✅ ¡Valuación Premium Completada!
          </div>
          
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '6px',
            marginBottom: '15px',
            position: 'relative'
          }}>
            <div style={{ fontSize: '14px', color: '#155724', marginBottom: '12px', fontWeight: '600', textAlign: 'center' }}>
              📊 Tu valuación está lista
              {valuationResults.isRent && ' (ARS y USD)'}
            </div>
            
            {/* Blurred Results Preview */}
            <div style={{
              filter: 'blur(8px)',
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              {valuationResults.isRent ? (
                // Show dual currency for rent
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#155724', textAlign: 'center', marginBottom: '8px' }}>
                    ARS ${valuationResults.preciosARS?.precioMinimo?.toLocaleString('es-AR')} - ${valuationResults.preciosARS?.precioMaximo?.toLocaleString('es-AR')}/mes
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#155724', textAlign: 'center' }}>
                    USD ${valuationResults.preciosUSD?.precioMinimo?.toLocaleString('es-AR')} - ${valuationResults.preciosUSD?.precioMaximo?.toLocaleString('es-AR')}/mes
                  </div>
                </div>
              ) : (
                // Show single USD value for sale
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724', textAlign: 'center' }}>
                  USD ${valuationResults.valorMinimo?.toLocaleString('es-AR')} - USD ${valuationResults.valorMaximo?.toLocaleString('es-AR')}
                </div>
              )}
            </div>
          </div>

          {/* Terms and Conditions Checkbox */}
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '2px solid #e9ecef'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#495057',
              lineHeight: '1.6'
            }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                style={{
                  width: '20px',
                  height: '20px',
                  marginTop: '2px',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              />
              <span>
                Acepto los{' '}
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#667eea', 
                    fontWeight: '600',
                    textDecoration: 'underline'
                  }}
                >
                  Términos y Condiciones
                </a>
                {' '}del servicio de Valuación Premium. Entiendo que esta es una estimación informativa y no una valuación oficial.
              </span>
            </label>
          </div>

          <button
            onClick={finalPrice === 0 ? handleFreePayment : goToPayment}
            disabled={!termsAccepted}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '17px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: termsAccepted ? (finalPrice === 0 ? '#10b981' : '#28a745') : '#6c757d',
              border: 'none',
              borderRadius: '8px',
              cursor: termsAccepted ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              boxShadow: termsAccepted ? '0 4px 12px rgba(40, 167, 69, 0.3)' : 'none',
              opacity: termsAccepted ? 1 : 0.6
            }}
            onMouseEnter={(e) => {
              if (termsAccepted) {
                e.target.style.backgroundColor = finalPrice === 0 ? '#059669' : '#218838';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(40, 167, 69, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (termsAccepted) {
                e.target.style.backgroundColor = finalPrice === 0 ? '#10b981' : '#28a745';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
              }
            }}
          >
            {finalPrice === 0 
              ? '✅ Ver Resultado Gratis' 
              : `💳 Pagar $${finalPrice?.toLocaleString('es-AR')} para Ver Resultados`}
          </button>
          
          <div style={{ fontSize: '12px', color: '#155724', marginTop: '12px', textAlign: 'center' }}>
            {finalPrice === 0 
              ? '🎉 ¡Servicio premium sin cargo gracias a tu código promocional!' 
              : promoCodeValid 
                ? `💎 Precio especial: $${finalPrice?.toLocaleString('es-AR')} (precio original: $${price?.toLocaleString('es-AR')})`
                : '💎 Accede al informe detallado con análisis de mercado completo'}
          </div>
        </div>
      )}

      {/* Payment Opened (for SMP) */}
      {paymentOpened && (
        <div style={{
          backgroundColor: '#e6f7ff',
          color: '#0066cc',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '15px',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          💳 <strong>Pago abierto en nueva pestaña</strong><br/>
          Completa el pago y vuelve a esta página para ver tus resultados.
        </div>
      )}

      {/* Button for non-premium or if valuation not complete */}
      {paymentType !== 'premium_valuation' && !loading && (
        <button
          onClick={createPayment}
          disabled={loading || !price}
          style={{
            width: '100%',
            padding: '15px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: loading ? '#6c757d' : '#00b4d8',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            if (!loading && price) {
              e.target.style.backgroundColor = '#0091ad';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && price) {
              e.target.style.backgroundColor = '#00b4d8';
            }
          }}
        >
          💳 Pagar Ahora
        </button>
      )}

      {/* Footer info */}
      {(paymentType !== 'premium_valuation' || !valuationComplete) && finalPrice > 0 && (
        <div style={{
          fontSize: '12px',
          color: '#6c757d',
          textAlign: 'center',
          marginTop: '10px',
          lineHeight: '1.4'
        }}>
          🔒 Pago seguro con MercadoPago<br/>
          Aceptamos tarjetas de crédito, débito y transferencias
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MercadoPagoPayment;
